// @react-native-async-storage/async-storage stand-in.
//
// The library's own web build writes every key to localStorage, which
// outlives the tab and the session on a shared computer. On the phone the
// same store holds the signed-in user's profile, letter drafts as they are
// typed, and legacy token keys. So here: everything goes to sessionStorage
// (gone when the tab closes), except a short allow-list of harmless flags
// that shape the next visit — "has signed in before", "email verified" —
// which go to localStorage. Keys are prefixed so nothing collides with the
// keychain shim (`fk.kc.`) or third-party scripts. Entries the library
// wrote before this shim existed are moved or removed once, on load.
//
// docs/PLAN_web-app.md §4.3: never localStorage for anything personal.

type Callback<T> = (error?: Error | null, result?: T) => void;
type KeyValuePair = [string, string | null];

const SESSION_PREFIX = 'fk.as.';
const PERSIST_PREFIX = 'fk.asp.';

/** Flags that may outlive the session: routing hints only, nothing personal. */
const PERSIST_KEYS = new Set([
  'hasLoggedIn',
  'hasCompletedOnboarding',
  '@email_verified',
  '@2fa_setup_complete',
  '@analytics_first_dashboard_seen',
  '@last_update_check',
  '@device_fallback_id',
]);
const PERSIST_KEY_PREFIXES = ['task_declined_'];

/** Unprefixed keys the library may have left in localStorage on older builds. */
const LEGACY_RAW_KEYS = new Set([
  'user',
  'token',
  'hasLoggedIn',
  'hasCompletedOnboarding',
  'pendingFeedback',
  'fcmToken',
  'biometricEnabled',
  'subscriptionState',
  'passwords',
  'vault_master_key',
  'vault_document_keys',
  'spouseCredentials',
  'loyaltyPoints',
  'contacts_data',
  'local_documents',
  'migration_v1_to_v2_state',
]);

function isPersisted(key: string): boolean {
  return PERSIST_KEYS.has(key) || PERSIST_KEY_PREFIXES.some((p) => key.startsWith(p));
}

function looksLikeAppKey(key: string): boolean {
  return key.startsWith('@') || LEGACY_RAW_KEYS.has(key) || PERSIST_KEY_PREFIXES.some((p) => key.startsWith(p));
}

function storageFor(key: string): Storage | null {
  try {
    return isPersisted(key) ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function storageKey(key: string): string {
  return (isPersisted(key) ? PERSIST_PREFIX : SESSION_PREFIX) + key;
}

/** Move allow-listed raw entries into the prefixed store; drop the rest. */
function migrateLegacyEntries(): void {
  try {
    const ls = window.localStorage;
    const raw: string[] = [];
    for (let i = 0; i < ls.length; i += 1) {
      const k = ls.key(i);
      if (k && !k.startsWith('fk.') && looksLikeAppKey(k)) raw.push(k);
    }
    for (const k of raw) {
      const value = ls.getItem(k);
      if (value !== null && isPersisted(k) && ls.getItem(PERSIST_PREFIX + k) === null) {
        ls.setItem(PERSIST_PREFIX + k, value);
      }
      ls.removeItem(k);
    }
  } catch {
    // No storage (private mode, blocked site data): nothing to migrate.
  }
}

migrateLegacyEntries();

function settle<T>(work: () => T, callback?: Callback<T>): Promise<T> {
  try {
    const result = work();
    callback?.(null, result);
    return Promise.resolve(result);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    callback?.(err);
    return Promise.reject(err);
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** The library's merge: JSON objects merged recursively, arrays concatenated. */
function mergeValues(existing: string | null, incoming: string): string {
  if (existing === null) return incoming;
  const a = JSON.parse(existing);
  const b = JSON.parse(incoming);
  const merge = (x: unknown, y: unknown): unknown => {
    if (isObject(x) && isObject(y)) {
      const out: Record<string, unknown> = { ...x };
      for (const [k, v] of Object.entries(y)) {
        if (v === undefined) continue;
        out[k] = merge(x[k], v);
      }
      return out;
    }
    if (Array.isArray(x) && Array.isArray(y)) return [...x, ...y];
    return y;
  };
  return JSON.stringify(merge(a, b));
}

function readItem(key: string): string | null {
  const s = storageFor(key);
  return s ? s.getItem(storageKey(key)) : null;
}

function writeItem(key: string, value: string): void {
  const s = storageFor(key);
  if (!s) throw new Error('Storage unavailable');
  s.setItem(storageKey(key), value);
}

function deleteItem(key: string): void {
  storageFor(key)?.removeItem(storageKey(key));
}

function allKeys(): string[] {
  const keys: string[] = [];
  const collect = (s: Storage, prefix: string) => {
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k && k.startsWith(prefix)) keys.push(k.slice(prefix.length));
    }
  };
  try {
    collect(window.sessionStorage, SESSION_PREFIX);
    collect(window.localStorage, PERSIST_PREFIX);
  } catch {
    // No storage: no keys.
  }
  return keys;
}

const AsyncStorage = {
  getItem: (key: string, callback?: Callback<string | null>) => settle(() => readItem(key), callback),
  setItem: (key: string, value: string, callback?: Callback<void>) =>
    settle(() => writeItem(key, value), callback),
  removeItem: (key: string, callback?: Callback<void>) => settle(() => deleteItem(key), callback),
  mergeItem: (key: string, value: string, callback?: Callback<void>) =>
    settle(() => writeItem(key, mergeValues(readItem(key), value)), callback),
  clear: (callback?: Callback<void>) =>
    settle(() => {
      for (const k of allKeys()) deleteItem(k);
    }, callback),
  getAllKeys: (callback?: Callback<readonly string[]>) => settle(() => allKeys(), callback),
  flushGetRequests: () => undefined,
  multiGet: (keys: readonly string[], callback?: Callback<readonly KeyValuePair[]>) =>
    settle(() => keys.map((k): KeyValuePair => [k, readItem(k)]), callback),
  multiSet: (pairs: readonly [string, string][], callback?: Callback<void>) =>
    settle(() => {
      for (const [k, v] of pairs) writeItem(k, v);
    }, callback),
  multiRemove: (keys: readonly string[], callback?: Callback<void>) =>
    settle(() => {
      for (const k of keys) deleteItem(k);
    }, callback),
  multiMerge: (pairs: readonly [string, string][], callback?: Callback<void>) =>
    settle(() => {
      for (const [k, v] of pairs) writeItem(k, mergeValues(readItem(k), v));
    }, callback),
};

export function useAsyncStorage(key: string) {
  return {
    getItem: (cb?: Callback<string | null>) => AsyncStorage.getItem(key, cb),
    setItem: (value: string, cb?: Callback<void>) => AsyncStorage.setItem(key, value, cb),
    mergeItem: (value: string, cb?: Callback<void>) => AsyncStorage.mergeItem(key, value, cb),
    removeItem: (cb?: Callback<void>) => AsyncStorage.removeItem(key, cb),
  };
}

export const __internal = { isPersisted, SESSION_PREFIX, PERSIST_PREFIX, migrateLegacyEntries };

export default AsyncStorage;
